import { Alert, Box, CircularProgress, Stack, Typography, palette } from '@munifocus/ui';
import { useTranslation } from 'react-i18next';
import type { FileActivityEntry } from '@munifocus/contracts-gov';
import { useFileActivity } from '../lib/files-api';
import { stageLabelKey } from '../lib/file-display';
import { frenchFirstOfMonth } from '@munifocus/utils';

function formatDateTime(iso: string, locale: string): string {
  return frenchFirstOfMonth(
    new Intl.DateTimeFormat(locale.startsWith('fr') ? 'fr-CA' : 'en-CA', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(iso)),
  );
}

/** Read a string off an entry's free-form metadata bag, or null. */
function metaString(entry: FileActivityEntry, key: string): string | null {
  const value = entry.metadata?.[key];
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * The proper nouns behind a step — the filename removed, the court file number,
 * the field whose deadline moved. Deliberately not localized: these are values
 * recorded off the file, and a hearing needs them as they were entered.
 */
function detailFor(entry: FileActivityEntry): string | null {
  return (
    metaString(entry, 'filename') ??
    metaString(entry, 'courtFileNumber') ??
    metaString(entry, 'reference') ??
    metaString(entry, 'title') ??
    metaString(entry, 'field') ??
    metaString(entry, 'key') ??
    null
  );
}

function ActivityRow({ entry, locale }: { entry: FileActivityEntry; locale: string }) {
  const { t } = useTranslation();

  // `file.stage_advanced` is the one action whose sentence names a stage, and
  // the stage is a code — so it is localized before interpolation rather than
  // printed raw into an otherwise translated sentence.
  const to = metaString(entry, 'to');
  const description = t(`gov.activity.actions.${entry.action.replace(/\./g, '_')}`, {
    defaultValue: t('gov.activity.unknownAction'),
    stage: to ? t(stageLabelKey(to as never)) : '',
    // A batch step names which batch action it was, localized like the stage.
    bulkAction: metaString(entry, 'bulkAction')
      ? t(`gov.activity.bulkActions.${metaString(entry, 'bulkAction')}`)
      : '',
  });

  const actorName = entry.actor.system
    ? t('gov.activity.system')
    : (entry.actor.name ?? entry.actor.email ?? t('gov.activity.unknownActor'));

  const detail = detailFor(entry);

  return (
    <Box component="li" sx={{ py: 1.25 }}>
      <Stack
        direction="row"
        sx={{ alignItems: 'baseline', justifyContent: 'space-between', gap: 2 }}
      >
        <Typography variant="body2">{description}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {formatDateTime(entry.createdAt, locale)}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {actorName}
      </Typography>
      {detail && (
        <Typography variant="caption" sx={{ display: 'block', color: palette.text.muted }}>
          {detail}
        </Typography>
      )}
    </Box>
  );
}

/**
 * The file's chain of custody: every mutating enforcement action recorded
 * against it, oldest first, with the officer who took it named. Read-only by
 * construction — the trail is append-only server-side and there is nothing here
 * to edit it with.
 */
export function FileActivity({ fileId }: { fileId: string }) {
  const { t, i18n } = useTranslation();
  const { data, isLoading, isError } = useFileActivity(fileId);

  return (
    <>
      <Typography variant="overline">{t('gov.activity.title')}</Typography>
      <Typography variant="body2" color="text.secondary">
        {t('gov.activity.description')}
      </Typography>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {isError && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {t('gov.activity.loadError')}
        </Alert>
      )}

      {data && data.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          {t('gov.activity.empty')}
        </Typography>
      )}

      {data && data.length > 0 && (
        <Box
          component="ol"
          aria-label={t('gov.activity.title')}
          sx={{
            listStyle: 'none',
            m: 0,
            p: 0,
            mt: 1,
            display: 'flex',
            flexDirection: 'column',
            // The separator is a border on the row itself rather than a
            // `<Divider />` between rows: an <ol> may only contain <li>, and a
            // divider element interleaved between them is markup a screen
            // reader has to reconcile against the item count it announces.
            '& > li + li': { borderTop: `1px solid ${palette.border.line}` },
          }}
        >
          {data.map((entry) => (
            <ActivityRow key={entry.id} entry={entry} locale={i18n.language} />
          ))}
        </Box>
      )}
    </>
  );
}
